import axios from "axios";
import { requestYachtRefresh } from "../context/YachtContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

// --- Token storage (localStorage fallback for Safari/iPad ITP) ---------------
// Safari (especially inside an iframe like our preview) blocks third-party
// HTTP-only cookies, so the SameSite=None; Secure access_token cookie set by
// /auth/login silently disappears on the next request. To keep mobile Safari
// users logged in we also stash the token returned in the login response body
// into localStorage and send it as an `Authorization: Bearer` header. The
// backend's `current_user` dependency already accepts that header as a
// fallback when no auth cookie is present.
const TOKEN_KEY = "seavanto_auth_token";
export const tokenStore = {
  get() {
    try { return localStorage.getItem(TOKEN_KEY) || ""; } catch { return ""; }
  },
  set(t) {
    try { if (t) localStorage.setItem(TOKEN_KEY, t); } catch {}
  },
  clear() {
    try { localStorage.removeItem(TOKEN_KEY); } catch {}
  },
};

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
  // 30s ceiling so a request that stalls (CORS preflight blocked by Safari
  // ITP, dropped by a flaky carrier proxy, etc.) eventually throws instead
  // of hanging the UI forever. iPad Safari users hit this exact failure
  // mode in production: the login XHR sat in "pending" with no error and
  // no resolution, leaving the Sign-in button frozen. With a timeout the
  // promise rejects and our error UI can show something concrete.
  timeout: 30000,
});

// Attach the stored token as a Bearer header on every request when present.
// We don't overwrite an explicit Authorization header if the caller already
// set one (e.g. one-off requests).
api.interceptors.request.use((cfg) => {
  const t = tokenStore.get();
  if (t) {
    cfg.headers = cfg.headers || {};
    if (!cfg.headers.Authorization && !cfg.headers.authorization) {
      cfg.headers.Authorization = `Bearer ${t}`;
    }
  }
  return cfg;
});

// When a yacht-scoped request comes back 403 we used to always refresh the
// yacht list (covers the "approver finalised deletion / you got kicked"
// scenario). But with the per-yacht rights matrix in place, a 403 can ALSO
// mean "you ARE a member but your role can't do this specific thing" — in
// which case refreshing yachts would just loop. We tell the two apart by
// inspecting the backend detail message.
api.interceptors.response.use(
  (resp) => resp,
  (err) => {
    const status = err?.response?.status;
    if (status === 401) {
      // Token is dead — wipe it so we don't keep sending an invalid Bearer.
      // The AuthContext's /auth/me check will then route the user to /login.
      const detail = (err?.response?.data?.detail || "").toString().toLowerCase();
      if (detail.includes("expired") || detail.includes("invalid") || detail.includes("not authenticated")) {
        tokenStore.clear();
      }
    }
    if (status === 403) {
      const detail = (err?.response?.data?.detail || "").toLowerCase();
      const looksLikeMembership = detail.includes("no access to this yacht");
      if (looksLikeMembership) {
        const cfg = err.config || {};
        const url = cfg.url || "";
        const hasYachtParam = !!(cfg.params && cfg.params.yacht_id) || /\byacht_id=/.test(url);
        const isYachtScoped = /\/yachts\//.test(url) || hasYachtParam;
        if (isYachtScoped) requestYachtRefresh("axios-403-membership");
      }
      // role-rights 403s ("Your role (X) is not permitted to …") are normal:
      // the caller is expected to handle them (e.g. show empty state).
    }
    return Promise.reject(err);
  },
);

// Fetch an auth-protected file as a same-origin blob URL using the NATIVE
// fetch() API (not axios/XHR). Returns the blob URL on 200, or null on 404 /
// any other non-OK status — without ever throwing.
//
// Why native fetch instead of `api.get(..., {responseType:'blob'})`? The
// runtime error-reporter monkey-patches XMLHttpRequest and reads
// `responseText` on completed requests; for a request configured with
// responseType='blob' that read throws an InvalidStateError, surfacing as an
// "Uncaught runtime errors" overlay. A derived video poster legitimately
// 404s for older videos, so we must fetch it on a path the reporter doesn't
// touch and swallow the miss silently.
export async function fetchFileBlobUrl(path) {
  if (!path) return null;
  try {
    const t = tokenStore.get();
    const res = await fetch(`${API}/files/${path}`, {
      credentials: "include",
      headers: t ? { Authorization: `Bearer ${t}` } : {},
    });
    if (!res.ok) return null;
    return URL.createObjectURL(await res.blob());
  } catch {
    return null;
  }
}


// Format FastAPI errors safely
export function fmtErr(e) {
  const d = e?.response?.data?.detail;
  if (d == null) return e?.message || "Something went wrong";
  if (typeof d === "string") return d;
  if (Array.isArray(d))
    return d.map((x) => x?.msg || JSON.stringify(x)).join(" ");
  if (typeof d === "object" && d.msg) return d.msg;
  return String(d);
}
